import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Shield, Clock, User, Search, Filter, RefreshCw } from "lucide-react";
import LayoutAdmin from "@/components/layout-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

interface AuditLogEntry {
  id: number;
  actorUserId?: number | null;
  actorName?: string;
  targetUserId?: number | null;
  organizationId?: number | null;
  action: string;
  resource: string;
  resourceId?: number | null;
  reason?: string | null;
  metadata?: any;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdAt: string;
}

const ACTION_COLORS: Record<string, string> = {
  created: "bg-green-100 text-green-800",
  user_created: "bg-green-100 text-green-800",
  updated: "bg-blue-100 text-blue-800",
  user_updated: "bg-blue-100 text-blue-800",
  user_deleted: "bg-red-100 text-red-800",
  impersonation_started: "bg-amber-100 text-amber-800",
  impersonation_stopped: "bg-amber-100 text-amber-800",
  status_changed_to_completed: "bg-green-100 text-green-800",
  status_changed_to_pending: "bg-yellow-100 text-yellow-800",
  status_changed_to_in_progress: "bg-blue-100 text-blue-800",
  status_changed_to_cancelled: "bg-gray-100 text-gray-800",
};

function getActionColor(action: string): string {
  return ACTION_COLORS[action] || "bg-slate-100 text-slate-700";
}

function formatAction(action: string): string {
  return action
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatResource(resource: string): string {
  return resource
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

function MetadataDisplay({ metadata }: { metadata: any }) {
  if (!metadata || typeof metadata !== "object" || Object.keys(metadata).length === 0) return null;
  return (
    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
      {Object.entries(metadata).map(([k, v]) => {
        if (typeof v === "object" && v !== null && "from" in (v as any)) {
          const change = v as { from: any; to: any };
          return (
            <span key={k} className="inline-block mr-2">
              <span className="font-medium capitalize">{k.replace(/_/g, " ")}</span>:{" "}
              <span className="line-through text-red-500">{String(change.from ?? "—")}</span>
              {" → "}
              <span className="text-green-600">{String(change.to ?? "—")}</span>
            </span>
          );
        }
        if (k === "passwordChanged") return <span key={k} className="text-amber-600">Password changed</span>;
        return (
          <span key={k} className="inline-block mr-2">
            <span className="font-medium capitalize">{k.replace(/_/g, " ")}</span>:{" "}
            <span>{String(v)}</span>
          </span>
        );
      })}
    </div>
  );
}

export default function AdminAuditLogs() {
  const [search, setSearch] = useState("");
  const [resourceFilter, setResourceFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [actorSearch, setActorSearch] = useState("");
  const [limit] = useState(200);

  const params = new URLSearchParams({ limit: String(limit) });
  if (resourceFilter !== "all") params.set("resource", resourceFilter);
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  if (actionFilter !== "all") params.set("action", actionFilter);

  const { data: logs = [], isLoading, refetch, isFetching } = useQuery<AuditLogEntry[]>({
    queryKey: ["/api/admin/audit-logs", resourceFilter, startDate, endDate, actionFilter, limit],
    queryFn: async () => {
      const res = await fetch(`/api/admin/audit-logs?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch audit logs");
      return res.json();
    },
  });

  const filtered = logs.filter(log => {
    const matchSearch = !search || (() => {
      const q = search.toLowerCase();
      return (
        log.action.toLowerCase().includes(q) ||
        log.resource.toLowerCase().includes(q) ||
        (log.actorName || "").toLowerCase().includes(q) ||
        String(log.resourceId || "").includes(q)
      );
    })();
    const matchActor = !actorSearch || (log.actorName || "").toLowerCase().includes(actorSearch.toLowerCase());
    return matchSearch && matchActor;
  });

  return (
    <LayoutAdmin>
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Audit Logs
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Track all account, job, and system activity across your organization
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="button-refresh-audit"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by action, resource, actor..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="input-audit-search"
                />
              </div>
              <Select value={resourceFilter} onValueChange={setResourceFilter}>
                <SelectTrigger className="w-[180px]" data-testid="select-audit-resource">
                  <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="All resources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Resources</SelectItem>
                  <SelectItem value="user_account">Users</SelectItem>
                  <SelectItem value="cleaning_job">Cleaning Jobs</SelectItem>
                  <SelectItem value="maintenance_job">Maintenance Jobs</SelectItem>
                  <SelectItem value="organization">Organization</SelectItem>
                </SelectContent>
              </Select>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[200px]" data-testid="select-audit-action">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="user_created">User Created</SelectItem>
                  <SelectItem value="user_updated">User Updated</SelectItem>
                  <SelectItem value="user_deleted">User Deleted</SelectItem>
                  <SelectItem value="password_reset">Password Reset</SelectItem>
                  <SelectItem value="impersonation_started">Impersonation Started</SelectItem>
                  <SelectItem value="impersonation_stopped">Impersonation Stopped</SelectItem>
                  <SelectItem value="cleaning_job_created">Cleaning Job Created</SelectItem>
                  <SelectItem value="maintenance_job_created">Maintenance Job Created</SelectItem>
                  <SelectItem value="maintenance_job_approved">Maintenance Approved</SelectItem>
                  <SelectItem value="maintenance_job_rejected">Maintenance Rejected</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative min-w-[150px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by actor..."
                  value={actorSearch}
                  onChange={e => setActorSearch(e.target.value)}
                  className="pl-9"
                  data-testid="input-audit-actor-search"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground whitespace-nowrap">From</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-[140px]"
                  data-testid="input-audit-start-date"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground whitespace-nowrap">To</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-[140px]"
                  data-testid="input-audit-end-date"
                />
              </div>
              {(startDate || endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStartDate(""); setEndDate(""); }}
                  data-testid="button-clear-date-filter"
                >
                  Clear dates
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">
              Activity Feed
              <span className="ml-2 text-muted-foreground font-normal text-sm">
                ({filtered.length} entries)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No audit log entries found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(log => (
                      <TableRow key={log.id} data-testid={`row-audit-${log.id}`}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3 shrink-0" />
                            {log.createdAt
                              ? format(new Date(log.createdAt), "MMM d, yyyy HH:mm")
                              : "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-sm">
                            <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="font-medium">
                              {log.actorName || "System"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`text-xs font-medium ${getActionColor(log.action)}`}
                            variant="outline"
                          >
                            {formatAction(log.action)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="capitalize">{formatResource(log.resource)}</span>
                            {log.resourceId && (
                              <span className="text-muted-foreground ml-1">#{log.resourceId}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <MetadataDisplay metadata={log.metadata} />
                          {!log.metadata && <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </LayoutAdmin>
  );
}
